import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Chip, Divider, Link, Stack} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { SearchDocument } from '../../../types/search/searchDocument';
import { GetSearchDocumentTypeTag } from '../../../utility/getSearchDocumentTypeTag';

interface Props {
    documents: SearchDocument[];
    searchInput: string;
}

const LegalDocumentList = (props:Props): JSX.Element => {

    const [documentList, setDocumentList] = useState<SearchDocument[]>([]);
    const [toggleViewMore, setToggleViewMore] = useState(false);

    useEffect(() => {
      setDocumentList(props.documents.slice(0,3));
      setToggleViewMore(props.documents.length > 3? true: false);
    }, [props.documents]);

    const handleViewMore = () => {
      setToggleViewMore(!toggleViewMore);
      setDocumentList(props.documents);
   }

   const copyDocumentLink = (documentId: number) => {
    let url = `https://www.ocpf.us/PublicSearch/ViewDocument?id=${documentId}`;
    navigator.clipboard.writeText(url);
    alert('Link copied and the document will be opened in the new tab.');
  }

    return (
     <Box>
        <Typography
            variant="h6"
            align={'left'}
            sx={{
            fontWeight: 700,
            }}
            color={'secondary'}
            paddingTop={3}
            gutterBottom
        >
        Legal Opinions and Reference Documents
        </Typography>
        <Divider textAlign="right">
            <Typography component={'i'} variant='body2' color="primary">{props.documents.length} total results</Typography>
        </Divider>
        <Grid container marginTop={2}>
          <Grid size={{xs:12, md:10}}>
            {documentList.map((document) => (
            <Box
            component={Accordion}
            key={document.id}
            paddingY={1}
            elevation={0}
            sx={{
              '&:first-of-type': {
                borderTopLeftRadius: 1,
                borderTopRightRadius: 1,
              },
              '&:not(:first-of-type):before': {
                opacity: '1 !important',
                display: 'block !important',
              },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <Box
              component={AccordionSummary}
              expandIcon={<ExpandMoreIcon />}
              padding={'0 !important'}
            >
              <Box>
                <Stack direction={'row'} spacing={1} marginBottom={1} alignItems={'center'}>
                    {GetSearchDocumentTypeTag(document.documentTypeId)}
                    <Typography component={'i'} variant='body2' color="text.secondary" marginTop={3} >
                      {document.date}
                    </Typography>
                </Stack>
                <Typography fontWeight={550} variant='subtitle1'>{document.code} {document.title}</Typography>
                <Typography color="text.secondary"><div dangerouslySetInnerHTML={{ __html: document.description }} /></Typography>               
              </Box>
            </Box>
            <AccordionDetails>
                <Box
                    width={1}
                    height={1}
                    padding={2}
                    component={Card}
                    boxShadow={0}
                    variant={'outlined'}
                >
                    <Grid container marginBottom={1}>
                        <Grid size={8}>
                            <Chip label="Full Text" color="primary" variant="outlined" />
                        </Grid>
                        {
                            document.url &&
                            <Grid size={2} marginTop={-1}>
                              <Button component={Link} href={document.url} target='_blank' variant="text" startIcon={<PictureAsPdfIcon />} color="primary">Download PDF</Button>
                            </Grid>
                        }
                        {
                          <Grid size={2} marginTop={-1}>
                            <Button 
                              component={Link} 
                              size="small" 
                              startIcon={<ContentCopyIcon />} 
                              href={`/PublicSearch/ViewDocument?id=${document.id}`} 
                              underline='none'
                              target="_blank" 
                              onClick={() => copyDocumentLink(document.id)}>
                                Copy Link
                            </Button>
                        </Grid>
                        }
                    </Grid>
                    <div dangerouslySetInnerHTML={{ __html: document.content.replace(new RegExp(`(${props.searchInput})`, 'gi'), (match:string) => `<mark>${match}</mark>`) }} />
                </Box>
            </AccordionDetails>
            </Box>
            ))}
          </Grid>
          {
            toggleViewMore && 
            <Grid size={10} display="flex" justifyContent="flex-end" marginTop={1}>
              <Link className='is-clickable' onClick={handleViewMore} underline='none' fontSize={"small"}  component={"i"}><i>View More Results</i></Link>
            </Grid>
          }
        </Grid>
  </Box>
  );
};

export default LegalDocumentList;